import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { SignupSections, SignupStep } from '@pages/Signup/Components/SignupStep'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useLegalLinksContext } from '@context/LegalLinksContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Link } from '@ui/components/Link'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { hasSubmittedEconomicProfileTest } from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TestAnswersDto } from '@model/ClientTestAnswerDto'

import { useLinksConfig } from '@hooks/useLinksConfig'

import { Box } from '@shared/components/Box'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { TestStatusTemplateInnerWrapper } from '@shared/templates/TestStatusTemplate/TestStatusTemplate'

import { SignupFormWrapper } from '../../Components/SignupFormWrapper'

export const AppTestRiskAcceptancePage: React.FC = () => {
  const { clientId } = useParams<{ clientId?: string }>()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()
  const locale = useSessionLanguage()
  const { refreshAccount } = useAccountWriteContext()
  const [answers, setAnswers] = useState<TestAnswersDto | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const { t } = useTranslation()
  const { links } = useLinksConfig()
  const { docLinks } = useLegalLinksContext()

  const { account } = useAccountReadContext()

  const isEconomicProfile = hasSubmittedEconomicProfileTest(account, product)
  const section = isEconomicProfile ? SignupSections.EconomicProfile : SignupSections.FinancialInfo

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        const a = await apiClient.getAccountAppTestAnswers(locale, product)
        if (!cancelled) setAnswers(a)
      } finally {
        setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [apiClient, locale, product])

  const handleAccept = async () => {
    if (!clientId || !answers?.id) return
    await apiClient.acceptAppTestRisk({ clientId, clientTestId: answers.id, riskAccepted: true })
    await refreshAccount(locale)
    navigate(`/sign-up/economic-profile/${clientId}`)
  }

  const handleDecline = async () => {
    if (clientId && answers?.id) {
      await apiClient.acceptAppTestRisk({ clientId, clientTestId: answers.id, riskAccepted: false })
    }
    navigate('/logout')
  }

  return (
    <SignupFormWrapper label={t('Sign up.Sign up')}>
      <TestStatusTemplateInnerWrapper>
        <SignupStep accountTypeId={account?.type.id} section={section} isCompleted />

        <Box flex fullWidth direction='column' gap='x32' align='center'>
          <ContentBlock
            variant='voucher'
            icon={<FeatureIcon size='lg' icon={WarningIcon} bg='surface-warning-high-em' />}
            title={t('Sign up.Proceed with caution')}
            description={[
              <Trans
                key='marginally-pass-message'
                i18nKey='Sign up.Marginally pass message'
                components={{
                  a: (
                    <Link
                      href={docLinks?.riskDisclosure ?? links.footer.TICKMILL_EU.riskDisclosure}
                      target='_blank'
                      size='lg'
                      rel='noopener noreferrer'
                    />
                  ),
                }}
              />,
            ]}
          />
          <FullScreenFooter>
            <Button.Primary fullWidth size='lg' onClick={handleAccept} disabled={loading}>
              {t('Yes I accept the Risk')}
            </Button.Primary>
            <Button.Secondary fullWidth size='lg' onClick={handleDecline}>
              {t('No I do not accept the Risk')}
            </Button.Secondary>
          </FullScreenFooter>
        </Box>
      </TestStatusTemplateInnerWrapper>
    </SignupFormWrapper>
  )
}
